import React from 'react';
import { Users, CheckCircle, MessageCircle, Mail, MapPin, Clock, Award, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

const VacancySection = () => {
  const benefits = [
    { text: 'Flexible hours — work as much as you can', icon: Clock },
    { text: 'Two prime Dublin locations (City Centre & Clarehall)', icon: MapPin },
    { text: 'Steady flow of clients from our online booking system', icon: Users },
    { text: 'Supportive, professional team environment', icon: Users },
    { text: 'Free parking & shower facilities at Clarehall', icon: CheckCircle },
    { text: 'Marketing & promotion handled for you', icon: CheckCircle },
  ];

  return (
    <section
      id="careers"
      className="py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0A1F19 0%, #16382E 50%, #0F2922 100%)' }}
      data-testid="vacancy-section"
    >
      <div className="absolute top-0 left-0 w-full h-full opacity-5"
        style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'1.5\' fill=\'%23D4AF37\'/%3E%3C/svg%3E")' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* Left — Content */}
          <div>
            <div className="inline-flex items-center gap-2 bg-luxury-gold/20 text-luxury-gold px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider mb-6 border border-luxury-gold/30">
              <Award size={14} className="fill-luxury-gold" />
              Careers
            </div>

            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-white mb-4 leading-tight">
              We're Hiring
              <span className="block text-luxury-gold italic mt-2 text-2xl sm:text-3xl">
                Massage Therapists Wanted
              </span>
            </h2>

            <p className="text-white/80 text-base sm:text-lg leading-relaxed mb-6">
              Dublin Holistic Massage is always looking for <strong className="text-white">highly talented male and female massage therapists</strong> to join our growing team.
              If you're passionate about wellness and helping people feel their best, we'd love to hear from you.
            </p>

            {/* Requirements */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 mb-6 border border-white/20">
              <p className="text-luxury-gold font-semibold mb-3 flex items-center gap-2">
                <Award size={16} />
                Requirements
              </p>
              <ul className="space-y-2.5 text-white/90 text-sm">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-luxury-gold mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">ITEC</strong> qualification desirable</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-luxury-gold mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">NRRI</strong> registration desirable</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-luxury-gold mt-0.5 flex-shrink-0" />
                  <span>Must be <strong className="text-white">self-employed</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-luxury-gold mt-0.5 flex-shrink-0" />
                  <span>Passionate about massage therapy and client care</span>
                </li>
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={() => window.open('https://wa.me/353857747474?text=Hi! I\'m a massage therapist and I\'m interested in joining Dublin Holistic Massage.', '_blank')}
                className="bg-luxury-green-600 hover:bg-luxury-green-500 text-white rounded-full py-5 px-6 text-sm font-medium group"
                data-testid="vacancy-whatsapp-btn"
              >
                <MessageCircle size={18} className="mr-2" />
                WhatsApp Us
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => window.open('mailto:dublinholisticmassage@gmail.com?subject=Massage Therapist Application&body=Hi, I am a massage therapist and I would like to apply to join Dublin Holistic Massage.', '_blank')}
                className="bg-luxury-gold hover:bg-luxury-gold/90 text-luxury-green rounded-full py-5 px-6 text-sm font-medium group"
                data-testid="vacancy-email-btn"
              >
                <Mail size={18} className="mr-2" />
                Email Your CV
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          {/* Right — Benefits */}
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-white/20">
            <h3 className="font-display text-2xl text-luxury-gold mb-6">
              Why Join Us?
            </h3>

            <div className="space-y-4">
              {benefits.map((b) => (
                <div key={b.text} className="flex items-start gap-3 group">
                  <div className="w-8 h-8 bg-luxury-gold/20 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-luxury-gold/30 transition-colors">
                    <b.icon size={16} className="text-luxury-gold" />
                  </div>
                  <span className="text-white/90 text-sm pt-1">{b.text}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-gradient-to-br from-luxury-gold/15 to-luxury-gold/5 rounded-2xl p-5 border border-luxury-gold/30 text-center">
              <p className="text-white font-display text-lg mb-1">Join a Team of 7+ Therapists</p>
              <p className="text-white/70 text-sm">Serving clients across Dublin since day one</p>
              <div className="flex justify-center gap-3 mt-3">
                <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-xs border border-white/20">Deep Tissue</span>
                <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-xs border border-white/20">Thai</span>
                <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-xs border border-white/20">Sports</span>
                <span className="bg-white/10 text-white/80 px-3 py-1 rounded-full text-xs border border-white/20">Relaxing</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default VacancySection;
